import { useDebounceValue } from 'noddi-util';
import { useEffect, useMemo, useState } from 'react';
import { CommonSelectProps, SelectBase, SelectOption } from './SelectBase';
import { getSelectOptionValue } from './selectOptionValue';

interface NoddiAsyncSelectProps<T = string> extends Omit<CommonSelectProps<T>, 'searchable'> {
  selectedOption?: SelectOption<T>;
  onSelectionChange: (option: SelectOption<T> | undefined) => void;
  onSearch: (searchTerm: string) => void;
  searchDebounceMs?: number;
  /** Shown in the search box on open, and sent before the user types. */
  initialSearch?: string;
}

export function NoddiAsyncSelect<T = string>(props: NoddiAsyncSelectProps<T>) {
  const {
    options = [],
    selectedOption,
    onSelectionChange,
    onSearch,
    searchDebounceMs = 300,
    onOpenChange,
    isLoading: externalLoading,
    initialSearch,
    ...baseSelectProps
  } = props;

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch ?? '');

  // Merge selectedOption with options to ensure selected items can be displayed

  const mergedOptions: SelectOption<T>[] = useMemo(() => {
    // Nothing is selected
    if (!selectedOption) {
      return options;
    }
    // Selected option is in the options array, no need to create map and array from map
    if (options.some((option) => option.value === selectedOption.value)) {
      return options;
    }

    const map = new Map<string, SelectOption<T>>();
    // Add selected option first
    map.set(String(selectedOption.value), selectedOption);
    // Add all other options
    options.forEach((option) => {
      map.set(String(option.value), option);
    });
    return Array.from(map.values());
  }, [options, selectedOption]);

  // Use the existing debounce hook
  const debouncedSearchTerm = useDebounceValue({
    value: searchTerm,
    delay: searchDebounceMs
  });

  // Determine if we're waiting for debounce (user is typing but debounced value hasn't updated yet)
  const isWaitingForDebounce = searchTerm !== debouncedSearchTerm;

  // Combine external loading with debounce waiting
  const isLoading = externalLoading || isWaitingForDebounce;

  // Fire search callback when debounced search term changes
  useEffect(() => {
    onSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearch]);

  const currentValue = selectedOption?.value;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);

    // initialSearch is the first query, not a one-shot. Every close returns
    // to it, so the next open is the seeded make or model again.
    if (!newOpen) {
      setSearchTerm(initialSearch ?? '');
    }
  };

  const handleValueChange = (newValue: string) => {
    const selectedNewOption = mergedOptions.find((option) => getSelectOptionValue(option) === newValue);
    if (selectedNewOption) {
      // Toggle: clear if same value, otherwise select the new option
      const finalOption = selectedOption?.value === selectedNewOption.value ? undefined : selectedNewOption;
      onSelectionChange(finalOption);
    } else {
      onSelectionChange(undefined);
    }
    // Selection does not go through the popover, so close here too.
    handleOpenChange(false);
  };

  return (
    <SelectBase
      {...baseSelectProps}
      options={mergedOptions}
      open={open}
      setOpen={setOpen}
      currentValue={currentValue}
      onValueChange={handleValueChange}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onOpenChange={handleOpenChange}
      isLocalFiltering={false} // Disable local filtering for async search
      isLoading={isLoading}
    />
  );
}
